import { type FC } from 'react'

import { DateTime } from 'luxon'

import { Button, Grid, Link, Typography } from '@mui/material'
import { MobileWrapper } from '@core/components/layout/MobileWrapper'
import {
  CustomInput,
  RHFCustomCheckbox,
  RHFCustomDatePicker,
  RHFCustomInput,
  RHFCustomSelect
} from '@core/components/ui'
import { useLayout } from '@core/contexts/layout'
import { colors } from '@core/helpers'
import { useRegisterForm } from '@hooks/useRegisterForm'

import { type RegisterFormData } from './registerSchema'
import { TermConditionsModal } from './TermConditionsModal'

interface Props {
  onRegister: (user: RegisterFormData) => Promise<void>
  enableMaxWidth?: boolean
}

export const RegisterForm: FC<Props> = ({ onRegister, enableMaxWidth = true }) => {
  const {
    control,
    documentTypes,
    errors,
    handleSubmit,
    lockInputs,
    onChangeAlphabeticInput,
    onChangePassword,
    onChangePhoneNumber,
    sexTypes,
    success,
    openTermConditonsModal,
    setOpenTermConditonsModal,
    values
  } = useRegisterForm()

  const { isMobile } = useLayout()

  return (
    <form onSubmit={handleSubmit(onRegister)}>
      <MobileWrapper maxWidth={enableMaxWidth ? undefined : '100%'} pb='2.4rem'>
        <Grid
          mb={isMobile ? '2.6rem' : '4.2rem'}
          container
          rowSpacing='1.6rem'
          columnSpacing={isMobile ? '1.6rem' : '2.4rem'}
        >
          <Grid item min={12} mb={!isMobile ? '0.8rem' : undefined}>
            <Typography variant='h1_bold' color='secondary' component='h1'>
              Registrate
            </Typography>
          </Grid>

          <Grid item min={6} display='flex' flexDirection='column'>
            <RHFCustomSelect
              control={control}
              options={documentTypes ?? []}
              label='Tipo de documento'
              name='documentType'
              disabled
            />
          </Grid>

          <Grid item min={6} display='flex' flexDirection='column'>
            <RHFCustomInput
              disabled
              label='Nº de documento'
              control={control}
              stateInput='success'
              name='documentNumber'
              type='number'
              inputMode='numeric'
            />
          </Grid>

          <Grid item min={12} display='flex' flexDirection='column'>
            <CustomInput
              disabled={lockInputs}
              label='Nombres'
              value={values.name}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
              onChange={value => onChangeAlphabeticInput('name', value)}
              name='name'
            />
          </Grid>

          <Grid item min={12} tablet_lg={6} display='flex' flexDirection='column'>
            <CustomInput
              disabled={lockInputs}
              label='Apellido paterno'
              error={Boolean(errors.lastname1)}
              helperText={errors.lastname1?.message}
              value={values.lastname1}
              onChange={value => onChangeAlphabeticInput('lastname1', value)}
              name='lastname1'
            />
          </Grid>

          <Grid item min={12} tablet_lg={6} display='flex' flexDirection='column'>
            <CustomInput
              disabled={lockInputs}
              label='Apellido materno'
              error={Boolean(errors.lastname2)}
              helperText={errors.lastname2?.message}
              value={values.lastname2}
              onChange={value => onChangeAlphabeticInput('lastname2', value)}
              name='lastname2'
            />
          </Grid>

          <Grid item min={12} tablet_lg={6}>
            <RHFCustomDatePicker
              control={control}
              name='born_date'
              fullWidth
              maxDate={DateTime.now()}
              disabled={lockInputs}
              label='Fecha de nacimiento'
            />
          </Grid>

          <Grid item min={12} tablet_lg={6} display='flex' flexDirection='column'>
            <RHFCustomSelect
              options={sexTypes ?? []}
              disabled={lockInputs}
              label='Sexo'
              control={control}
              name='sex'
            />
          </Grid>

          <Grid item min={12} tablet_lg={12} display='flex' flexDirection='column'>
            <RHFCustomInput
              label='Correo electrónico'
              stateInput={success.email ? 'success' : undefined}
              control={control}
              name='email'
            />
          </Grid>

          <Grid item min={12} display='flex' flexDirection='column'>
            <CustomInput
              label='Celular'
              value={values.phone_number}
              onChange={onChangePhoneNumber}
              name='phone_number'
              inputMode='numeric'
              stateInput={success.phone_number ? 'success' : undefined}
              error={Boolean(errors?.phone_number)}
              helperText={errors?.phone_number?.message}
            />
          </Grid>

          <Grid
            mb={!isMobile ? '0.8rem' : undefined}
            item
            min={12}
            display='flex'
            flexDirection='column'
          >
            <Typography mt='1.6rem' variant='h1_bold' color='secondary' component='h1'>
              Crea tu <span style={{ color: 'var(--color-primary)' }}>contraseña</span>
            </Typography>
          </Grid>

          <Grid item min={12} tablet_lg={6} display='flex' flexDirection='column'>
            <CustomInput
              name='password'
              label='Crea una contraseña'
              value={values.password}
              stateInput={success.password ? 'success' : undefined}
              onChange={value => onChangePassword('password', value)}
              error={Boolean(errors?.password)}
              isPassword
              helperText={errors?.password?.message}
              type='password'
            />
          </Grid>

          <Grid item min={12} tablet_lg={6} display='flex' flexDirection='column'>
            <CustomInput
              onChange={value => onChangePassword('confirm_password', value)}
              label='Confirma tu contraseña'
              stateInput={success.confirm_password ? 'success' : undefined}
              value={values.confirm_password}
              error={Boolean(errors?.confirm_password)}
              isPassword
              helperText={errors?.confirm_password?.message}
              name='confirm_password'
              type='password'
            />
          </Grid>

          <Grid
            my='1.2rem'
            item
            min={12}
            display='flex'
            flexDirection='row'
            gap={2}
            alignItems='center'
          >
            <RHFCustomCheckbox
              control={control}
              name='term_conditions'
              disableLabelClickeable
              label={
                <>
                  Acepto los{' '}
                  <Typography
                    component={Link}
                    onClick={() => setOpenTermConditonsModal(true)}
                    variant='body3'
                    color={colors.text_title}
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    términos y condiciones
                  </Typography>
                </>
              }
            />
          </Grid>

          <Grid item min={12} display='flex' flexDirection='column'>
            <Button size='large' type='submit'>
              Continuar
            </Button>
          </Grid>
        </Grid>
      </MobileWrapper>

      <TermConditionsModal
        control={control}
        isChecked={values.term_conditions}
        open={openTermConditonsModal}
        setOpen={setOpenTermConditonsModal}
      />
    </form>
  )
}
